import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { logoUrl } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

const Header = () => {
  const { lang, setLang, t } = useT();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
    };

    window.addEventListener('scroll', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navLinks = [
    {
      key: 'home',
      label: t.nav.home,
      href: '/',
    },
    {
      key: 'about',
      label: t.nav.about,
      href: '/about',
    },
    {
      key: 'services',
      label: t.nav.services,
      href: '/services',
    },
    {
      key: 'references',
      label: t.nav.references,
      href: '/references',
    },
    {
      key: 'international',
      label: t.nav.international,
      href: '/international',
    },
    {
      key: 'contact',
      label: t.nav.contact,
      href: '/contact',
    },
  ];

  const changeLanguage = (language) => {
    setLang(language);
    setOpen(false);
  };

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }

    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`
        fixed
        top-0
        left-0
        right-0
        z-50
        transition-all
        duration-300
        ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200'
            : 'bg-white/90 backdrop-blur-sm'
        }
      `}
    >

      {/* =========================================================
          HEADER CONTAINER
      ========================================================= */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">

        <div className="h-[90px] md:h-[96px] flex items-center">

          {/* =====================================================
              LOGO
          ===================================================== */}
          <Link
            to="/"
            className="flex items-center shrink-0"
            aria-label="GPE Énergies & Services"
          >
            <img
              src={logoUrl}
              alt="GPE Énergies & Services"
              className="h-16 sm:h-[68px] md:h-[72px] w-auto object-contain"
            />
          </Link>


          {/* =====================================================
              DESKTOP NAVIGATION
          ===================================================== */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-5 xl:gap-6 px-6">

            {navLinks.map((item) => {
              const active = isActive(item.href);

              return (
                <Link
                  key={item.key}
                  to={item.href}
                  className={`
                    relative
                    text-[13px]
                    font-medium
                    whitespace-nowrap
                    transition-colors
                    duration-200
                    ${
                      active
                        ? 'text-[#0b3b8c]'
                        : 'text-slate-700 hover:text-[#0b3b8c]'
                    }
                  `}
                >
                  {item.label}

                  <span
                    className={`
                      absolute
                      left-0
                      -bottom-2
                      h-[2px]
                      rounded-full
                      bg-[#0b3b8c]
                      transition-all
                      duration-200
                      ${
                        active
                          ? 'w-full opacity-100'
                          : 'w-0 opacity-0'
                      }
                    `}
                  />
                </Link>
              );
            })}

          </nav>


          {/* =====================================================
              DESKTOP RIGHT SIDE
          ===================================================== */}
          <div className="hidden lg:flex items-center shrink-0 gap-3">

            {/* =================================================
                LANGUAGE SWITCHER
            ================================================= */}
            <div className="flex items-center gap-1 text-xs font-bold whitespace-nowrap">

              {/* FR */}
              <button
                type="button"
                onClick={() => changeLanguage('fr')}
                className={`
                  px-2.5
                  py-1.5
                  rounded-md
                  transition-colors
                  duration-200
                  ${
                    lang === 'fr'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'text-slate-600 hover:text-[#0b3b8c] hover:bg-slate-100'
                  }
                `}
                aria-pressed={lang === 'fr'}
              >
                FR
              </button>

              <span className="text-slate-300">|</span>

              {/* EN */}
              <button
                type="button"
                onClick={() => changeLanguage('en')}
                className={`
                  px-2.5
                  py-1.5
                  rounded-md
                  transition-colors
                  duration-200
                  ${
                    lang === 'en'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'text-slate-600 hover:text-[#0b3b8c] hover:bg-slate-100'
                  }
                `}
                aria-pressed={lang === 'en'}
              >
                EN
              </button>

              <span className="text-slate-300">|</span>

              {/* AR */}
              <button
                type="button"
                onClick={() => changeLanguage('ar')}
                className={`
                  px-2.5
                  py-1.5
                  rounded-md
                  transition-colors
                  duration-200
                  ${
                    lang === 'ar'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'text-slate-600 hover:text-[#0b3b8c] hover:bg-slate-100'
                  }
                `}
                aria-pressed={lang === 'ar'}
              >
                AR
              </button>

            </div>


            {/* SEPARATOR */}
            <span className="h-5 w-px bg-slate-200" />


            {/* =================================================
                PHONE
            ================================================= */}
            <a
              href="tel:+33442072262"
              className="
                text-[13px]
                font-medium
                text-slate-600
                hover:text-[#0b3b8c]
                transition-colors
                whitespace-nowrap
              "
            >
              +33 4 42 07 22 62
            </a>


            {/* SEPARATOR */}
            <span className="h-5 w-px bg-slate-200" />


            {/* =================================================
                REQUEST QUOTE
            ================================================= */}
            <Link
              to="/contact"
              className="
                px-5
                py-2.5
                rounded-full
                text-[13px]
                font-semibold
                bg-[#0b3b8c]
                text-white
                hover:bg-[#082e6f]
                transition-colors
                shadow-md
                shadow-blue-900/20
                whitespace-nowrap
              "
            >
              {t.nav.cta}
            </Link>

          </div>


          {/* =====================================================
              MOBILE MENU BUTTON
          ===================================================== */}
          <button
            type="button"
            className="
              lg:hidden
              ml-auto
              w-10
              h-10
              rounded-lg
              flex
              items-center
              justify-center
              text-slate-800
              hover:bg-slate-100
              transition-colors
            "
            onClick={() => setOpen((value) => !value)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>

        </div>

      </div>


      {/* =========================================================
          MOBILE MENU
      ========================================================= */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg">

          <div className="px-5 sm:px-6 py-5">

            {/* MOBILE NAVIGATION */}
            <nav className="flex flex-col">

              {navLinks.map((item) => {
                const active = isActive(item.href);

                return (
                  <Link
                    key={item.key}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={`
                      py-3
                      border-b
                      border-slate-100
                      text-sm
                      font-medium
                      transition-colors
                      ${
                        active
                          ? 'text-[#0b3b8c]'
                          : 'text-slate-700 hover:text-[#0b3b8c]'
                      }
                    `}
                  >
                    {item.label}
                  </Link>
                );
              })}

            </nav>


            {/* MOBILE LANGUAGES */}
            <div className="flex items-center gap-2 pt-5">

              <button
                type="button"
                onClick={() => changeLanguage('fr')}
                className={`
                  px-4
                  py-2
                  rounded-md
                  text-xs
                  font-bold
                  transition-colors
                  ${
                    lang === 'fr'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'bg-slate-100 text-slate-700'
                  }
                `}
              >
                FR
              </button>

              <button
                type="button"
                onClick={() => changeLanguage('en')}
                className={`
                  px-4
                  py-2
                  rounded-md
                  text-xs
                  font-bold
                  transition-colors
                  ${
                    lang === 'en'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'bg-slate-100 text-slate-700'
                  }
                `}
              >
                EN
              </button>

              <button
                type="button"
                onClick={() => changeLanguage('ar')}
                className={`
                  px-4
                  py-2
                  rounded-md
                  text-xs
                  font-bold
                  transition-colors
                  ${
                    lang === 'ar'
                      ? 'bg-[#0b3b8c] text-white'
                      : 'bg-slate-100 text-slate-700'
                  }
                `}
              >
                AR
              </button>

            </div>


            {/* MOBILE PHONE */}
            <a
              href="tel:+33442072262"
              className="
                mt-4
                block
                text-sm
                font-medium
                text-slate-600
                hover:text-[#0b3b8c]
                transition-colors
              "
            >
              +33 4 42 07 22 62
            </a>


            {/* MOBILE CTA */}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="
                mt-4
                block
                w-full
                px-5
                py-3
                rounded-full
                text-sm
                font-semibold
                bg-[#0b3b8c]
                text-white
                text-center
                hover:bg-[#082e6f]
                transition-colors
              "
            >
              {t.nav.cta}
            </Link>

          </div>

        </div>
      )}

    </header>
  );
};

export default Header;